import { useContext, useEffect, useState } from 'react';
import { GRANULARITY_TYPES } from '../Header/GranularityToggler';
import usePmSensorAuthId from 'components/hooks/PM/usePmSensorAuthId';
import useRestoreResourceTypeIfNotExists from '../../useRestoreResourceTypeIfNotExists';
import { getResourceTypeDependencies } from '../../resource_types';
import useDefaultResourceType from '../../useDefaultResourceType';
import get from 'lodash/get';
import useRaWidgetConfigPayload from '../../settings/hooks/useRaWidgetsConfig/useRaWidgetConfigPayload';
import { CONFIG_WIDGETS_NAMES } from '../../settings/hooks/useRaWidgetsConfig/useRaWidgetsConfig';
import { ResourceAllocationFiltersContext } from '../../../state/filters/contexts';
import useResourceAllocationService from 'common/services/resource-allocation.service.versions';
import { useDebounceCallback } from '@react-hook/debounce';
import useAutoGranularity from '../../../state/filters/hooks/useAutoGranularity';

const DEFAULT_QUERY_STRING_PARAM_NAME =
  'defaultAllocationOverTimeDropdownValue';
const useAllocationOverTime = () => {
  const {
    state: { period, filtersPayload, extraPayload, isInitialized }
  } = useContext(ResourceAllocationFiltersContext);
  const raService = useResourceAllocationService();

  const defaultResourceType = useDefaultResourceType(
    DEFAULT_QUERY_STRING_PARAM_NAME
  );
  const [resourceType, setResourceType] = useState(defaultResourceType);
  const pmSensorAuthId = usePmSensorAuthId();
  const [data, setData] = useState([]);
  const [granularity, setGranularity] = useAutoGranularity({
    period,
    granularityTypes: GRANULARITY_TYPES
  });
  const [epicsPercent, setEpicsPercent] = useState(null);
  const [isOverlapping, setIsOverlapping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const widgetConfigPayload = useRaWidgetConfigPayload(
    CONFIG_WIDGETS_NAMES.OVER_TIME_CHART
  );

  useRestoreResourceTypeIfNotExists({
    resourceType,
    restoreFn: () => setResourceType(defaultResourceType)
  });

  const fetchAllocationOverTime = useDebounceCallback(async () => {
    try {
      const res = await raService.getOverTimeChart({
        sensor_authorization_id: pmSensorAuthId,
        before: period.before,
        after: period.after,
        type: resourceType.type,
        issue_type: resourceType.issueType,
        ...(resourceType.id && { id: resourceType.id }),
        ...(resourceType.extraParams && {
          extraParams: resourceType.extraParams
        }),
        filtersPayload,
        aggregation_ranges: granularity.apiValue,
        limit: widgetConfigPayload.limit,
        extraPayload
      });

      const resData = get(res, 'data', []);
      const resEpicsPercent = get(res, 'share_entities_fte');
      const isOverlapping = get(res, 'is_overlapping', false);
      setData(resData);
      setEpicsPercent(resEpicsPercent);
      setIsOverlapping(isOverlapping);
    } catch (e) {
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (!isInitialized) return;
    setIsLoading(true);
    fetchAllocationOverTime();
  }, [
    isInitialized,
    granularity,
    period.before,
    period.after,
    filtersPayload,
    ...getResourceTypeDependencies(resourceType)
  ]);

  return {
    granularity,
    setGranularity,
    resourceType,
    setResourceType,
    data,
    epicsPercent,
    isOverlapping,
    isLoading
  };
};

export default useAllocationOverTime;
